/**
 * The screens everyone at a table sees while the venue staff decide on it (Indra, 2026-09-26). Once the host has sent the table, the
 * host's and the players' phones all show the same "waiting for the staff" screen with nothing to tap; when the staff answer, each
 * phone shows it — the host gets an OK button that opens the scoring screen, the players' screens open by themselves once the host
 * has. Every card carries `data-table-screen`, which is how the polling loops know a table screen is still on the page.
 */

import { renderBrandHeader } from '../branding';
import { esc } from '../helpers';

export type TableRole = 'host' | 'guest';

const playerList = (players: string[]): string =>
  `<ul class="stack table-lineup">${players.map((p, i) => `<li><span class="muted">${i + 1}</span> ${esc(p)}</li>`).join('')}</ul>`;

/** Sent to the staff: the host and the players see the same thing, and nothing on it can be tapped. */
export function renderTableWaitingApproval(root: HTMLElement, opts: { tableName: string; players: string[]; role: TableRole }): void {
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card table-hero" data-table-screen="awaiting">
      <div class="hero-tile" aria-hidden="true">🀄</div>
      <h1>Your table is in the spotlight!</h1>
      <p>
        The venue staff are taking a look at <strong>${esc(opts.tableName)}</strong> and getting ready to give it the green light.
        ${opts.role === 'host' ? 'Your table is locked in while they check.' : 'Sit tight — your host has sent the table in.'}
      </p>
      <div class="table-dots" role="status" aria-label="Waiting for the staff"><span></span><span></span><span></span></div>
      <h2>Tonight's line-up</h2>
      ${playerList(opts.players)}
      <p class="muted">
        Nothing to tap here — this screen changes by itself the moment the staff answer. Get your tiles ready!
      </p>
    </div>
  `;
}

/** The staff approved. The host gets the OK button; the players just wait for the host (their screen then opens by itself). */
export function renderTableApproved(
  root: HTMLElement,
  opts: { role: TableRole; tableName: string; byStaff: boolean; onOk?: () => void },
): void {
  const host = opts.role === 'host';
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card table-hero table-hero-yes" data-table-screen="approved">
      <div class="hero-tile" aria-hidden="true">🎉</div>
      <h1>${opts.byStaff ? 'Approved — game on!' : 'Game on!'}</h1>
      <p>
        ${
          opts.byStaff
            ? `The staff gave <strong>${esc(opts.tableName || 'your table')}</strong> the green light. The leaderboard is calling — shuffle up and take your seats!`
            : 'Your leaderboard game is ready. Shuffle up and take your seats!'
        }
      </p>
      ${
        host
          ? `<p class="muted">Press OK and your scoring screen opens — the players' screens open right after yours.</p>
             <button type="button" id="tblOkBtn" style="width:100%;margin-top:0.5rem">OK — let's play!</button>`
          : `<div class="table-dots" role="status" aria-label="Waiting for the host"><span></span><span></span><span></span></div>
             <p class="muted">Your host is opening the scoring screen — yours opens by itself in a moment.</p>`
      }
    </div>
  `;
  if (host) {
    const btn = root.querySelector<HTMLButtonElement>('#tblOkBtn')!;
    btn.addEventListener('click', () => {
      btn.disabled = true;
      opts.onOk?.();
    });
  }
}

/** The staff said not yet. The host can take the table back; the players wait for the host. */
export function renderTableDeclined(root: HTMLElement, opts: { role: TableRole; onBack?: () => void }): void {
  const host = opts.role === 'host';
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card table-hero" data-table-screen="declined">
      <div class="hero-tile" aria-hidden="true">✋</div>
      <h1>Not approved just yet</h1>
      <p>
        The staff couldn't give this table the green light this time. ${
          host
            ? 'Have a quick word with them, then take your table back and send it in again.'
            : 'Your host will sort it out with them — stay on this screen.'
        }
      </p>
      ${host ? '<button type="button" id="tblBackBtn" style="width:100%;margin-top:0.5rem">Back to my table</button>' : ''}
    </div>
  `;
  if (host) {
    const btn = root.querySelector<HTMLButtonElement>('#tblBackBtn')!;
    btn.addEventListener('click', () => {
      btn.disabled = true;
      opts.onBack?.();
    });
  }
}
